import React, { useEffect, useState } from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useApiListAuthSession } from '../../../../../api-new/state/auth'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ColumnDateTime, ColumnFormat, ColumnMenu, ColumnText, ColumnYesNo } from '../../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../../shared/menu/menu'
import { IconLogout } from '../../../../../shared/icon/icon'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { serverMessage } from '../../../../../shared/sso-workspace/format'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { useAuthContext } from '../../../../../context/auth.context'
import { OTHER_SESSIONS_STAY, SIGN_OUT_SESSION_TITLE, signOutSessionBody, YOUR_OTHER_SESSIONS_STAY } from '../../../../../shared/session/sign-out'

// How a session was signed in: "LMS launch via <connection>" for a learner's
// bounded LMS session (Phase F: auth's `lmsLaunch`, already in words), the
// connection's name for single sign-on, the trade-show or self-register PIN,
// or an EducationXR account (password or another device; the session doesn't
// record which).
export const signedInWith = session =>
  session.lmsLaunch
    ? session.lmsLaunch
    : session.providerID
      ? session.providerName || 'Single sign-on'
      : session.isPinAuthenticated
        ? 'PIN'
        : 'EducationXR sign-in'

// The confirmation's words live in shared/session/sign-out.js (every
// "sign out this session" says the same); re-exported for the pages here.
export { OTHER_SESSIONS_STAY, SIGN_OUT_SESSION_TITLE, signOutSessionBody }

// Signing out one of your own sessions: auth ends the whole session the row
// is (DELETE /auth-session/:id/self). The session you're using is marked and
// is signed out with Sign out, like the staff Sessions page.
export const endSession = async (session, { remove, reload }) => {
  await remove(`${session.id}/self`)
  await reload()
}

export const LoginSession = () => {
  const authContext = useAuthContext()

  const apiListAuthSession = useApiListAuthSession()

  const [ending, setEnding] = useState(null)
  const [busy, setBusy] = useState(false)
  const [endError, endFailure, setEndError] = useErrorLine()

  const reload = () => apiListAuthSession.get(`${authContext.state.loginID}/login`)

  const handleReady = () => {
    if (authContext.state.loginID) {
      reload()
    }
  }

  const handleDestroyed = () => apiListAuthSession.init()

  useEffect(
    () => {
      if (authContext.state.loginID) {
        reload()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authContext]
  )

  const handleConfirm = async () => {
    setBusy(true)
    setEndError(null)
    try {
      await endSession(ending, { remove: id => apiListAuthSession.delete(id), reload })
    } catch (err) {
      setEndError(serverMessage(err, "The session couldn't be signed out."), err)
      setBusy(false)
      return
    }
    setBusy(false)
    setEnding(null)
  }

  return (
    <React.Fragment>
      <BreadcrumbView text="login sessions">
        {/* Newest-relevant first: the session you're using sorts to the top. */}
        <TableAgClient apiList={apiListAuthSession} sortDesc onReady={handleReady} onDestoryed={handleDestroyed}>
          <ColumnYesNo field="isCurrentSession" header="Current Session" />
          <ColumnFormat field="providerName" header="Signed in with" render={row => (row.data ? signedInWith(row.data) : null)} />
          <ColumnText field="organization" header="Organization" />
          <ColumnDateTime field="createdAt" header="Started" />
          <ColumnDateTime field="lastActiveAt" header="Last active" />
          <ColumnDateTime field="expiresOn" header="Expires" />
          <ColumnText field="ipAddress" header="IP" />
          <ColumnText field="ipLocation" header="Location" />
          <ColumnText field="app" />
          <ColumnText field="appVersion" />
          <ColumnText field="deviceName" />
          <ColumnText field="deviceType" />
          <ColumnText field="deviceOS" />
          <ColumnText field="devicePlatform" />
          <ColumnMenu
            menu={row =>
              row.isCurrentSession
                ? <span className="sw-hint small text-nowrap">This session</span>
                : <Menu small>
                    <MenuItem Icon={IconLogout} text="sign out" pin show onClick={() => { setEndError(null); setEnding(row) }} />
                  </Menu>}
          />
        </TableAgClient>
      </BreadcrumbView>

      <ConfirmDialog
        open={Boolean(ending)}
        title={SIGN_OUT_SESSION_TITLE}
        body={`${signOutSessionBody('you')} ${YOUR_OTHER_SESSIONS_STAY}`}
        confirmText="Sign out"
        busy={busy}
        error={endError}
        failure={endFailure}
        onConfirm={handleConfirm}
        onCancel={() => setEnding(null)}
      />
    </React.Fragment>
  )
}
